import { memo, useCallback, useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import {
    Alert,
    Autocomplete,
    Box,
    Button,
    FormControl,
    InputLabel,
    MenuItem,
    Select,
    Stack,
    Tab,
    Tabs,
    TextField,
    Tooltip,
    Typography
} from '@mui/material';
import Iconify from '../../components/iconify';
import Label from '../../components/label';
import { TONES } from '../../config/statusTones';
import { formatApiErrors, hasError } from '../../utils/formatError';
import {
    CORE_ACTIONS,
    KIND_ACTIONS,
    parseJsonObjectText,
    previewActions,
    proxyChoiceBody,
    proxyOptions,
    sameProxyChoice,
    withExampleInput
} from '../../utils/scraperScenarios';
import i18n from '../../i18n';
import CodeEditor from './CodeEditor';
import LiveBrowserView from './LiveBrowserView';
import PreviewLog from './PreviewLog';
import PreviewResult from './PreviewResult';

// The gateway API whose settings rows can lend a preview their endpoint, proxy and
// stored input (gateway_apis.name, as core resolves the driver).
const XSCRAPER_API = 'xscraper';

// Enough to scroll back through a run; more is a browser tab holding megabytes
// of console spam.
const MAX_LOG_LINES = 1000;

// Run phases: the label each shows and the status tone it is drawn in.
const PHASES = {
    starting: { label: 'scraperScenarios.statusStarting', tone: 'inFlight' },
    queued: { label: 'scraperScenarios.statusQueued', tone: 'inFlight' },
    running: { label: 'scraperScenarios.statusRunning', tone: 'inFlight' },
    done: { label: 'scraperScenarios.statusDone', tone: 'done' },
    error: { label: 'scraperScenarios.statusError', tone: 'failed' },
    cancelled: { label: 'scraperScenarios.statusCancelled', tone: 'neutral' },
    stopped: { label: 'scraperScenarios.statusStopped', tone: 'neutral' },
    paused: { label: 'scraperScenarios.statusPaused', tone: 'inFlight' }
};
const ACTIVE_PHASES = ['starting', 'queued', 'running', 'paused'];

// The banner over a paused run, by why it paused.
const PAUSE_MESSAGES = {
    start: 'scraperScenarios.pausedBefore',
    step: 'scraperScenarios.pausedBefore',
    breakpoint: 'scraperScenarios.pausedAtBreakpoint',
    end: 'scraperScenarios.pausedEnd',
    error: 'scraperScenarios.pausedError'
};

// What core adds to each action's input, and what it expects back.
const ACTION_HINTS = {
    payin_init: 'scraperScenarios.hintPayinInit',
    payout_make: 'scraperScenarios.hintPayoutMake'
};

// A proxy's state, as the picker shows it (public.proxy_status).
const PROXY_STATUS = {
    active: 'scraperScenarios.proxyActive',
    failed: 'scraperScenarios.proxyFailed'
};

// Remembered per scenario in this browser: the run input, settings row, action and proxy.
const storageKey = (scenarioId) => `scraperScenarioPreview:${scenarioId ?? 'new'}`;

const readStored = (scenarioId) => {
    try {
        const parsed = JSON.parse(localStorage.getItem(storageKey(scenarioId)) || 'null');
        return parsed && typeof parsed === 'object' ? parsed : {};
    } catch {
        return {};
    }
};

const writeStored = (scenarioId, value) => {
    try {
        localStorage.setItem(storageKey(scenarioId), JSON.stringify(value));
    } catch {
        // Private mode or a full quota: remembering is a convenience.
    }
};

const textOf = (value) => {
    if (typeof value === 'string') return value;
    try {
        return JSON.stringify(value);
    } catch {
        return String(value);
    }
};

/** Seconds since `startedAt`, ticking while the run is going. Its own component, so only it re-renders. */
function Elapsed({ startedAt, endedAt }) {
    const [now, setNow] = useState(() => Date.now());

    useEffect(() => {
        if (!startedAt || endedAt) return undefined;
        const timer = setInterval(() => setNow(Date.now()), 100);
        return () => clearInterval(timer);
    }, [startedAt, endedAt]);

    if (!startedAt) return null;
    const ms = Math.max(0, (endedAt || now) - startedAt);
    return (
        <Typography variant="body2" color="text.secondary" sx={{ fontVariantNumeric: 'tabular-nums' }}>
            {`${(ms / 1000).toFixed(1)} s`}
        </Typography>
    );
}

Elapsed.propTypes = {
    startedAt: PropTypes.number,
    endedAt: PropTypes.number
};

/**
 * Runs the scenario's unsaved code in x-scraper and shows the run as it happens:
 * the remote browser, its log, and what it returned.
 *
 * Optionally borrows an xscraper gateway settings row: its scraper endpoint and
 * proxy, and one action's stored input — which holds the bank credentials, so the
 * server layers it in and it never reaches the browser. A payin row's action is
 * payin_init, a payout row's payout_make. Without a row, the run gets the template
 * and the run input only.
 *
 * The proxy is the row's list's, or none, unless another list or one proxy is
 * chosen — by id: their credentials stay on the server, and the run's log names the
 * proxy it went out through without them.
 *
 * Stopping is aborting the request: the server sees the connection close and
 * cancels the job. Leaving the page does the same.
 *
 * `getSource()` returns the editor's current `{ code, inputTemplateText }`. It is
 * asked at Run time rather than passed down, so typing in the editor does not
 * re-render the panel. `runRef.current` is set to the Run action, for the page's
 * Ctrl/Cmd+Enter.
 *
 * Debug runs the code step by step — a step is a browser action — pausing before
 * the first and at the breakpoints set in the code editor; Record runs it to its
 * end. Either stays paused with the browser open until Finish, and while paused
 * the live view drives the page: with recording on, every click and key comes back
 * as a line of code put into the editor (`debuggerRef.current`, scenarioDebugger).
 * Commands and input go out one at a time, in order, on their own requests; the
 * stream, which the server renews every few minutes (`rotate`), is reattached.
 */
function ScenarioPreviewPanel({
    server,
    settingsServer,
    scenarioId,
    editorKey,
    getSource,
    canRun,
    canReadSettings,
    runRef,
    debuggerRef,
    proxyListsServer,
    proxiesServer,
    canReadProxyLists,
    canReadProxies
}) {
    const [stored] = useState(() => readStored(scenarioId));
    const [inputText, setInputText] = useState(typeof stored.input === 'string' ? stored.input : '{}');
    const [settingsId, setSettingsId] = useState(stored.gatewaySettingsId ?? null);
    const [action, setAction] = useState(typeof stored.action === 'string' ? stored.action : '');
    const [proxyChoice, setProxyChoice] = useState(
        stored.proxy && typeof stored.proxy === 'object' ? stored.proxy : null
    );
    const [proxyChoices, setProxyChoices] = useState([]);
    const [proxyChoicesLoaded, setProxyChoicesLoaded] = useState(false);

    const [settingsRows, setSettingsRows] = useState([]);
    const [settingsLoaded, setSettingsLoaded] = useState(false);

    const [phase, setPhase] = useState('idle');
    const [position, setPosition] = useState(null);
    const [url, setUrl] = useState('');
    const [done, setDone] = useState(null);
    const [failure, setFailure] = useState(null);
    const [refusal, setRefusal] = useState(null);
    const [startedAt, setStartedAt] = useState(null);
    const [endedAt, setEndedAt] = useState(null);
    const [tab, setTab] = useState('log');
    const [logView, setLogView] = useState({ lines: [], dropped: 0 });
    const [runMode, setRunMode] = useState('run');
    const [paused, setPaused] = useState(null);
    const [recording, setRecording] = useState(false);
    const [selectPrompt, setSelectPrompt] = useState(null);

    const liveRef = useRef(null);
    const abortRef = useRef(null);
    const runIdRef = useRef(0);
    const runStartRef = useRef(0);
    const logRef = useRef({ lines: [], dropped: 0, nextId: 0 });
    const flushRef = useRef(0);
    const mountedRef = useRef(true);
    // The interactive run being driven: its job, and the queue its commands and
    // input wait in so they reach x-scraper in the order they were given.
    const jobRef = useRef(null);
    const queueRef = useRef(Promise.resolve());

    const active = ACTIVE_PHASES.includes(phase);
    const settingsRow = settingsRows.find((item) => item.id === settingsId) || null;
    const actions = settingsRow ? previewActions(settingsRow.settings, settingsRow.kind) : CORE_ACTIONS;

    useEffect(
        () => () => {
            mountedRef.current = false;
            abortRef.current?.abort();
            cancelAnimationFrame(flushRef.current);
        },
        []
    );

    // Keyed by scenarioId, which is in the deps: a scenario created from this
    // page keeps its panel, and what was set up for it moves from "new" to its id.
    useEffect(() => {
        writeStored(scenarioId, { input: inputText, gatewaySettingsId: settingsId, action, proxy: proxyChoice });
    }, [scenarioId, inputText, settingsId, action, proxyChoice]);

    useEffect(() => {
        // Without the run permission the panel shows only a note.
        if (!canRun || !canReadSettings) return undefined;
        let cancelled = false;
        settingsServer
            .getAllFiltered([], [{ field: 'name', direction: 'asc' }], {
                prefilter: [{ field: 'isArchived', op: '!=', value: true }]
            })
            .then((result) => {
                if (cancelled) return;
                // Filtered here rather than in the request: the full DTO carries
                // the API's name, and the settings for the action list.
                const list = hasError(result)
                    ? []
                    : (result.data || []).filter((item) => item.gatewayApi?.name === XSCRAPER_API);
                setSettingsRows(list);
                setSettingsLoaded(true);
            });
        return () => {
            cancelled = true;
        };
    }, [canRun, canReadSettings, settingsServer]);

    useEffect(() => {
        if (!canRun || (!canReadProxyLists && !canReadProxies) || !proxyListsServer || !proxiesServer) return undefined;
        let cancelled = false;
        Promise.all([
            canReadProxyLists ? proxyListsServer.getLight([], [{ field: 'name', direction: 'asc' }]) : null,
            canReadProxies ? proxiesServer.getAllFiltered([], [{ field: 'id', direction: 'asc' }]) : null
        ]).then(([lists, proxies]) => {
            if (cancelled) return;
            const rows = (result) => (!result || hasError(result) ? [] : result.data || []);
            setProxyChoices(proxyOptions(rows(lists), rows(proxies)));
            setProxyChoicesLoaded(true);
        });
        return () => {
            cancelled = true;
        };
    }, [canRun, canReadProxyLists, canReadProxies, proxyListsServer, proxiesServer]);

    // A remembered proxy or list that is gone: back to the default.
    useEffect(() => {
        if (proxyChoicesLoaded && proxyChoice && !proxyChoices.some((option) => sameProxyChoice(option, proxyChoice))) {
            setProxyChoice(null);
        }
    }, [proxyChoicesLoaded, proxyChoices, proxyChoice]);

    // A remembered settings row that is gone — archived, or not xscraper.
    useEffect(() => {
        if (settingsLoaded && settingsId !== null && !settingsRows.some((item) => item.id === settingsId)) {
            setSettingsId(null);
        }
    }, [settingsLoaded, settingsRows, settingsId]);

    const pushLine = useCallback((kind, text, level) => {
        const log = logRef.current;
        log.nextId += 1;
        log.lines.push({ id: log.nextId, at: Date.now() - runStartRef.current, kind, level, text });
        if (log.lines.length > MAX_LOG_LINES) {
            const excess = log.lines.length - MAX_LOG_LINES;
            log.lines.splice(0, excess);
            log.dropped += excess;
        }
        // At most one render per animation frame, however chatty the page's
        // console is.
        if (!flushRef.current) {
            flushRef.current = requestAnimationFrame(() => {
                flushRef.current = 0;
                setLogView({ lines: [...logRef.current.lines], dropped: logRef.current.dropped });
            });
        }
    }, []);

    const finish = useCallback((next) => {
        setPhase(next);
        setEndedAt(Date.now());
    }, []);

    const stop = useCallback(() => {
        abortRef.current?.abort();
    }, []);

    // One command or input for the running job, after everything sent before it.
    // A refusal is logged: the run goes on, as it does in x-scraper.
    const enqueue = useCallback(
        (send) => {
            const job = jobRef.current;
            if (!job) return;
            queueRef.current = queueRef.current.then(async () => {
                // Not started yet (no job event), or over: nothing to send it to.
                if (jobRef.current !== job || !job.id) return;
                const result = await send(job.id);
                if (jobRef.current !== job) return;
                if (hasError(result)) {
                    pushLine('error', i18n.t('scraperScenarios.logRefused', { message: formatApiErrors(result).join(' ') }));
                } else if (result.data) {
                    setRecording(Boolean(result.data.recording));
                }
            });
        },
        [pushLine]
    );

    const command = useCallback(
        (body) => {
            // Keys typed just before reach the page before the command does.
            liveRef.current?.flushInput();
            enqueue((jobId) => server.previewControl(jobId, body));
        },
        [enqueue, server]
    );

    const sendInput = useCallback(
        (input) => enqueue((jobId) => server.previewInput(jobId, input)),
        [enqueue, server]
    );

    const chooseOption = useCallback(
        (value) => {
            setSelectPrompt(null);
            sendInput({ type: 'select', value });
        },
        [sendInput]
    );

    const start = useCallback(async (mode) => {
        if (!canRun) return;
        const { code, inputTemplateText } = getSource();
        const template = parseJsonObjectText(inputTemplateText);
        const input = parseJsonObjectText(inputText);

        const problems = [];
        if (!code || !code.trim()) problems.push(i18n.t('scraperScenarios.runNeedsCode'));
        if (template.error) {
            problems.push(
                template.error === 'notObject'
                    ? i18n.t('scraperScenarios.templateNotObject')
                    : i18n.t('scraperScenarios.templateInvalid')
            );
        }
        if (input.error) problems.push(i18n.t('scraperScenarios.runInputInvalid'));
        if (settingsRow && !action) problems.push(i18n.t('scraperScenarios.runNeedsAction'));
        if (problems.length) {
            setRefusal(problems);
            return;
        }

        // A run started over one in progress replaces it. The old one's late
        // events and its end are told apart by the run id and ignored.
        abortRef.current?.abort();
        runIdRef.current += 1;
        const runId = runIdRef.current;
        const current = () => mountedRef.current && runIdRef.current === runId;

        const controller = new AbortController();
        abortRef.current = controller;
        const interactive = mode !== 'run';
        const editorDebugger = interactive ? debuggerRef?.current : null;
        const breakpoints = editorDebugger ? editorDebugger.startRun() : [];
        const job = interactive ? { id: null, lastTs: 0, recorded: new Set(), rotated: false } : null;
        jobRef.current = job;
        queueRef.current = Promise.resolve();
        runStartRef.current = Date.now();
        logRef.current = { lines: [], dropped: 0, nextId: logRef.current.nextId };
        setLogView({ lines: [], dropped: 0 });
        liveRef.current?.reset();
        setRefusal(null);
        setFailure(null);
        setDone(null);
        setUrl('');
        setPosition(null);
        setTab('log');
        setStartedAt(runStartRef.current);
        setEndedAt(null);
        setPhase('starting');
        setRunMode(mode);
        setPaused(null);
        setRecording(mode === 'record');
        setSelectPrompt(null);

        let ended = false;
        const handleEvent = (type, data) => {
            if (!current()) return;
            const event = data && typeof data === 'object' ? data : {};
            if (job && typeof event.ts === 'number') job.lastTs = Math.max(job.lastTs, event.ts);
            switch (type) {
            case 'job':
                // Sent again on every reattach, with the same id.
                if (job && job.id) break;
                if (job) job.id = event.jobId;
                pushLine('status', i18n.t('scraperScenarios.logJob', { id: event.jobId }));
                pushLine('status', event.proxy
                    ? i18n.t('scraperScenarios.logProxy', { proxy: event.proxy.proxy, list: event.proxy.listName })
                    : i18n.t('scraperScenarios.logNoProxy'));
                break;
            case 'step':
                pushLine('step', i18n.t('scraperScenarios.logStep', {
                    line: event.line ?? '?',
                    call: [event.call, event.arg].filter(Boolean).join(' ')
                }));
                break;
            case 'paused':
                setPaused(event);
                setPhase('paused');
                editorDebugger?.showPaused(event.line, { failed: event.reason === 'error', reason: event.reason });
                pushLine(event.reason === 'error' ? 'error' : 'paused', i18n.t(PAUSE_MESSAGES[event.reason] || 'scraperScenarios.pausedBefore', {
                    line: event.line ?? '?',
                    call: event.call || '',
                    message: event.message || ''
                }));
                break;
            case 'resumed':
                setPaused(null);
                setSelectPrompt(null);
                setPhase('running');
                editorDebugger?.clearPaused();
                break;
            case 'recorded':
                if (!job || job.recorded.has(event.id)) break;
                job.recorded.add(event.id);
                pushLine('recorded', event.code || '');
                if (!editorDebugger?.insertRecorded(event)) {
                    pushLine('status', i18n.t('scraperScenarios.logRecordedNotInserted'));
                }
                break;
            case 'select':
                setSelectPrompt({ selector: event.selector, options: Array.isArray(event.options) ? event.options : [] });
                break;
            case 'rotate':
                if (job) job.rotated = true;
                break;
            case 'status':
                if (event.status === 'pending') {
                    setPhase('queued');
                    setPosition(event.position ?? null);
                    pushLine('status', i18n.t('scraperScenarios.logQueued', { position: event.position ?? '?' }));
                } else if (event.status === 'running') {
                    setPhase('running');
                    pushLine('status', i18n.t('scraperScenarios.logRunning'));
                } else {
                    pushLine('status', textOf(event.status));
                }
                break;
            case 'frame':
                liveRef.current?.pushFrame(event);
                break;
            case 'url':
                setUrl(event.url || '');
                pushLine('url', event.url || '');
                break;
            case 'log':
                pushLine('log', textOf(event.message));
                break;
            case 'console':
                pushLine('console', textOf(event.text), event.type);
                break;
            case 'pageerror':
                pushLine('pageerror', textOf(event.message));
                break;
            case 'done':
                ended = true;
                setDone(event);
                pushLine('result', i18n.t('scraperScenarios.logDone'));
                setTab('result');
                finish('done');
                break;
            case 'error':
                ended = true;
                setFailure(textOf(event.message ?? data));
                pushLine('error', textOf(event.message ?? data));
                finish('error');
                break;
            case 'cancelled':
                ended = true;
                pushLine('status', i18n.t('scraperScenarios.logCancelled', { reason: textOf(event.reason ?? '') }));
                finish('cancelled');
                break;
            default:
                break;
            }
        };

        const body = { code, inputTemplate: template.value, input: input.value };
        if (scenarioId) body.scenarioId = scenarioId;
        if (interactive) {
            body.mode = mode;
            if (breakpoints.length) body.breakpoints = breakpoints;
        }
        // Together or not at all: the action names which of the row's stored
        // inputs to layer in. The row on screen, not the remembered id: one that
        // is not in the list (not loaded, or not readable) is not used.
        if (settingsRow) {
            body.gatewaySettingsId = settingsRow.id;
            body.action = action;
        }
        // The option on screen, not the remembered one: a choice no longer listed is not used.
        Object.assign(body, proxyChoiceBody(proxyChoices.find((option) => sameProxyChoice(option, proxyChoice))));

        // Breakpoints toggled mid-run apply to it at once.
        const unsubscribe = editorDebugger?.onBreakpointsChange((lines) => command({ command: 'breakpoints', lines }));

        let result = await server.preview(body, { signal: controller.signal, onEvent: handleEvent });
        // The server ends an interactive run's stream short of the edge's time
        // limit, leaving the job running: carry on from the last event seen.
        while (job && job.rotated && !ended && current() && !result.aborted && !hasError(result)) {
            job.rotated = false;
            pushLine('status', i18n.t('scraperScenarios.logReattached'));
            // eslint-disable-next-line no-await-in-loop
            result = await server.reattach(job.id, job.lastTs, { signal: controller.signal, onEvent: handleEvent });
        }
        unsubscribe?.();
        if (!current()) return;
        abortRef.current = null;
        if (job) {
            jobRef.current = null;
            setPaused(null);
            setSelectPrompt(null);
            editorDebugger?.endRun();
        }

        // done / error / cancelled already said how the run went; whatever the
        // connection did after that does not change it.
        if (ended) return;
        if (result.aborted) {
            pushLine('status', i18n.t('scraperScenarios.logStopped'));
            finish('stopped');
        } else if (hasError(result)) {
            // Refused before it started (422/400), or the connection broke.
            setRefusal(formatApiErrors(result));
            finish('error');
        } else {
            setFailure(i18n.t('scraperScenarios.streamEndedEarly'));
            finish('error');
        }
    }, [canRun, getSource, inputText, settingsRow, action, proxyChoice, proxyChoices, scenarioId, server, pushLine, finish, debuggerRef, command]);

    const run = useCallback(() => start('run'), [start]);

    useEffect(() => {
        if (!runRef) return undefined;
        runRef.current = run;
        return () => {
            runRef.current = null;
        };
    }, [runRef, run]);

    // A script that has ended (or failed) is finished by either command.
    const scriptOver = paused?.reason === 'end' || paused?.reason === 'error';
    const resume = useCallback(() => command({ command: 'continue' }), [command]);
    const step = useCallback(() => command({ command: 'step' }), [command]);

    // F8 continues and F10 steps while paused, Monaco focused or not — taken in
    // the capture phase, before Monaco's own F8 (next problem).
    useEffect(() => {
        if (!paused) return undefined;
        const onKeyDown = (event) => {
            if (event.ctrlKey || event.metaKey || event.altKey) return;
            if (event.key !== 'F8' && event.key !== 'F10') return;
            event.preventDefault();
            event.stopPropagation();
            if (event.key === 'F8' || scriptOver) resume();
            else step();
        };
        window.addEventListener('keydown', onKeyDown, true);
        return () => window.removeEventListener('keydown', onKeyDown, true);
    }, [paused, scriptOver, resume, step]);

    const chooseSettings = (next) => {
        setSettingsId(next ? next.id : null);
        // The action core calls on the row's kind, unless the one chosen is the
        // row's too: moving from a payin row to a payout row moves to payout_make.
        if (next && !previewActions(next.settings, next.kind).includes(action)) {
            setAction(KIND_ACTIONS[next.kind] || previewActions(next.settings, next.kind)[0]);
        }
    };

    const exampleText = action ? withExampleInput(inputText, action) : null;

    const hint = (() => {
        const lines = [];
        if (action) {
            lines.push(
                ACTION_HINTS[action]
                    ? i18n.t(ACTION_HINTS[action])
                    : i18n.t('scraperScenarios.hintOtherAction')
            );
        }
        lines.push(
            settingsRow && action
                ? i18n.t('scraperScenarios.hintLayering', { settings: settingsRow.name, action })
                : i18n.t('scraperScenarios.hintNoSettings')
        );
        return lines;
    })();

    if (!canRun) {
        return (
            <Alert severity="info" sx={{ m: 2 }}>
                {i18n.t('scraperScenarios.runNotAllowed')}
            </Alert>
        );
    }

    const phaseInfo = PHASES[phase];
    const tone = phaseInfo ? TONES[phaseInfo.tone] : null;

    return (
        <Stack spacing={1.5} sx={{ p: 2 }}>
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1}>
                <Autocomplete
                    size="small"
                    sx={{ flex: 1, minWidth: 0 }}
                    options={settingsRows}
                    value={settingsRow}
                    disabled={!canReadSettings}
                    onChange={(_event, next) => chooseSettings(next)}
                    getOptionLabel={(option) => option.name || ''}
                    isOptionEqualToValue={(option, value) => option.id === value.id}
                    renderOption={({ key: _key, ...props }, option) => (
                        <li {...props} key={option.id}>
                            <Box sx={{ minWidth: 0 }}>
                                <Typography variant="body2">{option.name}</Typography>
                                <Typography variant="caption" color="text.secondary" noWrap component="div">
                                    {[option.kind && i18n.t(`enums.${option.kind}`), option.settings?.endpoint]
                                        .filter(Boolean)
                                        .join(' · ')}
                                </Typography>
                            </Box>
                        </li>
                    )}
                    renderInput={(params) => (
                        <TextField
                            {...params}
                            label={i18n.t('scraperScenarios.settings')}
                            placeholder={i18n.t('scraperScenarios.noSettings')}
                            helperText={
                                !canReadSettings
                                    ? i18n.t('scraperScenarios.settingsNotReadable')
                                    : settingsLoaded && settingsRows.length === 0
                                        ? i18n.t('scraperScenarios.noXscraperSettings')
                                        : undefined
                            }
                        />
                    )}
                />
                <FormControl size="small" sx={{ width: { xs: '100%', sm: 200 } }}>
                    <InputLabel id="scraper-preview-action">{i18n.t('scraperScenarios.action')}</InputLabel>
                    <Select
                        labelId="scraper-preview-action"
                        label={i18n.t('scraperScenarios.action')}
                        value={action}
                        onChange={(event) => setAction(event.target.value)}
                        sx={{ fontFamily: 'monospace' }}
                    >
                        {settingsRow === null && (
                            <MenuItem value="">
                                <em>{i18n.t('scraperScenarios.noAction')}</em>
                            </MenuItem>
                        )}
                        {actions.map((key) => (
                            <MenuItem key={key} value={key} sx={{ fontFamily: 'monospace' }}>
                                {key}
                            </MenuItem>
                        ))}
                        {/* A remembered action this row does not list, kept selectable rather than silently blank. */}
                        {action && !actions.includes(action) && (
                            <MenuItem value={action} sx={{ fontFamily: 'monospace' }}>
                                {action}
                            </MenuItem>
                        )}
                    </Select>
                </FormControl>
            </Stack>

            <Autocomplete
                size="small"
                options={proxyChoices}
                value={proxyChoices.find((option) => sameProxyChoice(option, proxyChoice)) || null}
                disabled={!canReadProxyLists && !canReadProxies}
                onChange={(_event, next) => setProxyChoice(next ? { kind: next.kind, id: next.id } : null)}
                groupBy={(option) => i18n.t(option.kind === 'list' ? 'scraperScenarios.proxyLists' : 'scraperScenarios.proxies')}
                getOptionLabel={(option) => option.label || ''}
                isOptionEqualToValue={sameProxyChoice}
                renderOption={({ key: _key, ...props }, option) => (
                    <li {...props} key={`${option.kind}-${option.id}`}>
                        <Box sx={{ minWidth: 0 }}>
                            <Typography variant="body2" sx={{ fontFamily: option.kind === 'proxy' ? 'monospace' : undefined }}>
                                {option.label}
                            </Typography>
                            <Typography variant="caption" color="text.secondary" noWrap component="div">
                                {option.kind === 'list'
                                    ? i18n.t('scraperScenarios.proxyListCounts', {
                                        active: option.activeCount,
                                        failed: option.failedCount
                                    })
                                    : [option.listName, PROXY_STATUS[option.status] && i18n.t(PROXY_STATUS[option.status])]
                                        .filter(Boolean)
                                        .join(' · ')}
                            </Typography>
                        </Box>
                    </li>
                )}
                renderInput={(params) => {
                    const selected = proxyChoices.find((option) => sameProxyChoice(option, proxyChoice));
                    return (
                        <TextField
                            {...params}
                            label={i18n.t('scraperScenarios.proxy')}
                            placeholder={
                                settingsRow?.proxyList
                                    ? i18n.t('scraperScenarios.proxyDefaultSettings', { list: settingsRow.proxyList.name })
                                    : i18n.t('scraperScenarios.proxyDefaultNone')
                            }
                            InputLabelProps={{ ...params.InputLabelProps, shrink: true }}
                            helperText={
                                !canReadProxyLists && !canReadProxies
                                    ? i18n.t('scraperScenarios.proxiesNotReadable')
                                    : selected?.kind === 'list'
                                        ? i18n.t('scraperScenarios.proxyHintList')
                                        : selected?.status === 'failed'
                                            ? i18n.t('scraperScenarios.proxyHintFailed')
                                            : undefined
                            }
                        />
                    );
                }}
            />

            <Stack spacing={0.25}>
                {hint.map((line) => (
                    <Typography key={line} variant="caption" color="text.secondary">
                        {line}
                    </Typography>
                ))}
            </Stack>

            <Stack direction="row" alignItems="center" justifyContent="space-between">
                <Typography variant="subtitle2">{i18n.t('scraperScenarios.runInput')}</Typography>
                <Tooltip title={i18n.t('scraperScenarios.fillExampleHint')}>
                    <span>
                        <Button
                            size="small"
                            disabled={!exampleText}
                            startIcon={<Iconify icon="eva:flash-outline" />}
                            onClick={() => exampleText && setInputText(exampleText)}
                        >
                            {i18n.t('scraperScenarios.fillExample')}
                        </Button>
                    </span>
                </Tooltip>
            </Stack>
            <CodeEditor
                language="json"
                path={`scraper-preview-input-${editorKey}.json`}
                value={inputText}
                onChange={setInputText}
                ariaLabel={i18n.t('scraperScenarios.runInput')}
                sx={{ height: 120 }}
            />

            <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
                {active ? (
                    <>
                        {paused && (
                            <Tooltip title={i18n.t(scriptOver ? 'scraperScenarios.finishHint' : 'scraperScenarios.continueShortcut')}>
                                <Button
                                    variant="contained"
                                    startIcon={<Iconify icon={scriptOver ? 'eva:checkmark-circle-2-outline' : 'eva:play-circle-outline'} />}
                                    onClick={resume}
                                >
                                    {i18n.t(scriptOver ? 'scraperScenarios.finish' : 'scraperScenarios.continue')}
                                </Button>
                            </Tooltip>
                        )}
                        {paused && !scriptOver && (
                            <Tooltip title={i18n.t('scraperScenarios.stepShortcut')}>
                                <Button variant="outlined" startIcon={<Iconify icon="eva:arrowhead-right-outline" />} onClick={step}>
                                    {i18n.t('scraperScenarios.step')}
                                </Button>
                            </Tooltip>
                        )}
                        {runMode !== 'run' && !paused && phase === 'running' && (
                            <Tooltip title={i18n.t('scraperScenarios.pauseHint')}>
                                <Button
                                    variant="outlined"
                                    startIcon={<Iconify icon="eva:pause-circle-outline" />}
                                    onClick={() => command({ command: 'pause' })}
                                >
                                    {i18n.t('scraperScenarios.pause')}
                                </Button>
                            </Tooltip>
                        )}
                        {runMode !== 'run' && (
                            <Tooltip title={i18n.t('scraperScenarios.recordToggleHint')}>
                                <Button
                                    variant={recording ? 'contained' : 'outlined'}
                                    color="error"
                                    startIcon={<Iconify icon="eva:radio-button-on-fill" />}
                                    onClick={() => command({ command: 'record', on: !recording })}
                                >
                                    {i18n.t(recording ? 'scraperScenarios.recordingOn' : 'scraperScenarios.recordingOff')}
                                </Button>
                            </Tooltip>
                        )}
                        <Button
                            variant={paused ? 'text' : 'contained'}
                            color="error"
                            startIcon={<Iconify icon="eva:stop-circle-outline" />}
                            onClick={stop}
                        >
                            {i18n.t('scraperScenarios.stop')}
                        </Button>
                    </>
                ) : (
                    <>
                        <Tooltip title={i18n.t('scraperScenarios.runShortcut')}>
                            <Button
                                variant="contained"
                                startIcon={<Iconify icon="eva:play-circle-outline" />}
                                onClick={run}
                            >
                                {i18n.t('scraperScenarios.run')}
                            </Button>
                        </Tooltip>
                        <Tooltip title={i18n.t('scraperScenarios.debugHint')}>
                            <Button
                                variant="outlined"
                                startIcon={<Iconify icon="eva:activity-outline" />}
                                onClick={() => start('debug')}
                            >
                                {i18n.t('scraperScenarios.debug')}
                            </Button>
                        </Tooltip>
                        <Tooltip title={i18n.t('scraperScenarios.recordHint')}>
                            <Button
                                variant="outlined"
                                color="error"
                                startIcon={<Iconify icon="eva:radio-button-on-fill" />}
                                onClick={() => start('record')}
                            >
                                {i18n.t('scraperScenarios.record')}
                            </Button>
                        </Tooltip>
                    </>
                )}
                {phaseInfo && (
                    <Label color={tone.color} dot={tone.dot}>
                        {i18n.t(phaseInfo.label, { position: position ?? '?' })}
                    </Label>
                )}
                <Elapsed startedAt={startedAt} endedAt={endedAt} />
            </Stack>

            {refusal && (
                <Alert severity="error" onClose={() => setRefusal(null)}>
                    <Stack component="span" spacing={0.5}>
                        {refusal.map((line, index) => (
                            <Typography key={`${index}-${line}`} component="span" variant="body2">
                                {line}
                            </Typography>
                        ))}
                    </Stack>
                </Alert>
            )}
            {failure && (
                <Alert severity="error" sx={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                    {failure}
                </Alert>
            )}
            {paused && (
                <Alert
                    severity={paused.reason === 'error' ? 'error' : paused.reason === 'end' ? 'success' : 'warning'}
                    sx={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}
                >
                    {i18n.t(PAUSE_MESSAGES[paused.reason] || 'scraperScenarios.pausedBefore', {
                        line: paused.line ?? '?',
                        call: paused.call || '',
                        message: paused.message || ''
                    })}
                    {' '}
                    {i18n.t(recording ? 'scraperScenarios.pausedRecordingHint' : 'scraperScenarios.pausedDriveHint')}
                </Alert>
            )}

            <LiveBrowserView
                ref={liveRef}
                url={url}
                live={active}
                interactive={Boolean(paused)}
                recording={recording && Boolean(paused)}
                onInput={sendInput}
                selectPrompt={selectPrompt}
                onSelect={chooseOption}
                onDismissSelect={() => setSelectPrompt(null)}
            />

            <Box>
                <Tabs value={tab} onChange={(_event, next) => setTab(next)} sx={{ minHeight: 36, mb: 1 }}>
                    <Tab value="log" label={i18n.t('scraperScenarios.tabLog')} sx={{ minHeight: 36 }} />
                    <Tab value="result" label={i18n.t('scraperScenarios.tabResult')} disabled={!done} sx={{ minHeight: 36 }} />
                </Tabs>
                {tab === 'log' || !done ? (
                    <PreviewLog lines={logView.lines} dropped={logView.dropped} sx={{ height: 260 }} />
                ) : (
                    <PreviewResult done={done} action={action} sx={{ height: 260 }} />
                )}
            </Box>
        </Stack>
    );
}

ScenarioPreviewPanel.propTypes = {
    server: PropTypes.object.isRequired,
    settingsServer: PropTypes.object.isRequired,
    scenarioId: PropTypes.number,
    editorKey: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
    getSource: PropTypes.func.isRequired,
    canRun: PropTypes.bool,
    canReadSettings: PropTypes.bool,
    runRef: PropTypes.object,
    debuggerRef: PropTypes.object,
    proxyListsServer: PropTypes.object,
    proxiesServer: PropTypes.object,
    canReadProxyLists: PropTypes.bool,
    canReadProxies: PropTypes.bool
};

export default memo(ScenarioPreviewPanel);
